//  ----------      START   IMPORT  BLOCK       ----------
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
//  --- Widgets ---
import { Modal, Button, Form, ButtonGroup, Collapse, Row, Col, Card } from 'react-bootstrap'; 

import WhitelistUserSearchBar from '../../../WhitelistUserSearchBar/WhitelistUserSearchBar';
//  --- Context  ---
import { useLanguage } from '../../../../API/Context/LanguageContext';
import { useUserAgenda } from '../../../../API/Hooks/useUserAgenda';
//  --- Config   ---
import { VocabAppointments } from '../../../../ConfigFiles/multilingual/VocabAppointments';
import { VocabButtons } from '../../../../ConfigFiles/multilingual/VocabButtons';
//  --- Utils   ---
import { dateFormats } from '../../../../ConfigFiles/date_formats';
//  ----------      END   IMPORT  BLOCK       ----------

const dateToInputFormat = (date) => {
    if (!date) return '';
    return DateTime.fromJSDate(date).toFormat(dateFormats.date_YMDtHM);
};

const AppointmentFormModal = ({ isOpen, onClose, initialDates, onSuccess }) => {
    const { lan } = useLanguage();
    const { createProfessionalAppointment, isLoading: isCreating, searchMaskedPatientName } = useUserAgenda();

    const [isNetworkPatient, setIsNetworkPatient] = useState(true);

    const [formData, setFormData] = useState({
        title: 'Cita',
        networkUsername: '',
        manualName: '',
        manualPhoneNumber: '',
        start: initialDates ? dateToInputFormat(initialDates.start) : '',
        end: initialDates ? dateToInputFormat(initialDates.end) : '',
        location: '', 
        description: '',
        tags: '',
    });

    // Reset form state when initial dates change or modal opens
    useEffect(() => {
        if (initialDates) {
            const startFormat = dateToInputFormat(initialDates.start);
            
            // Calculate end date: 30 minutes after start date
            let endFormat = dateToInputFormat(initialDates.end);
            if (initialDates.start) {
                const startDt = DateTime.fromJSDate(initialDates.start);
                endFormat = startDt.plus({ minutes: 30 }).toFormat(dateFormats.date_YMDtHM);
            }

            setFormData(prevData => ({
                ...prevData, 
                title: 'Cita',
                networkUsername: '',
                manualName: '',
                manualPhoneNumber: '',
                start: startFormat,
                end: endFormat,
            }));
        }
    }, [initialDates]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'start') {
            const startDt = DateTime.fromFormat(value, dateFormats.date_YMDtHM);
            
            if (startDt.isValid) {
                const calculatedEnd = startDt.plus({ minutes: 30 }).toFormat(dateFormats.date_YMDtHM);
                setFormData(prev => ({
                    ...prev,
                    start: value,
                    end: calculatedEnd,
                }));
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleTogglePatientType = (isNetwork) => {
        setIsNetworkPatient(isNetwork);
    };

    // Clear selected network patient
    const handleClearSelectedPatient = () => {
        setFormData(prev => ({
            ...prev,
            networkUsername: '',
            manualName: ''
        }));
    };

    const handleCreate = async (e) => {
    e.preventDefault();

    // Get current local zone (or user-selected timezone string)
    const userZone = DateTime.local().zoneName;

    // 1. Explicitly parse in local zone so input hours match local intent
    const startDt = DateTime.fromFormat(formData.start, dateFormats.date_YMDtHM, { zone: userZone });
    const endDt = DateTime.fromFormat(formData.end, dateFormats.date_YMDtHM, { zone: userZone });
    
    if (!startDt.isValid || !endDt.isValid) {
        alert('Please check your date and time inputs.');
        return;
    }

    // DYNAMIC ATTENDEE PAYLOAD CONSTRUCTION
    let attendeePayload;

    if (isNetworkPatient) {
        if (!formData.networkUsername) {
            alert('Please search and select a network patient.');
            return;
        }
        attendeePayload = {
            username: formData.networkUsername,
        };
    } else {
        if (!formData.manualName || !formData.manualPhoneNumber) {
            alert('Please enter the patient name and phone number.');
            return;
        }
        attendeePayload = {
            name: formData.manualName,
            phone_number: formData.manualPhoneNumber,
        };
    }
    
    // 2. Map payload to snake_case `time_zone` expected by Pydantic
    const payload = {
        title: formData.title || 'Cita',
        start: startDt.toUTC().toISO(), 
        end: endDt.toUTC().toISO(),
        time_zone: userZone,
        location: formData.location, 
        description: formData.description,
        tags: formData.tags,
        attendee: attendeePayload, 
    };

    try {
        console.log('Payload being sent:', payload);
        const transformedEvent = await createProfessionalAppointment(payload);
        onSuccess(transformedEvent); 
        onClose();
    } catch (error) {
        console.error("Appointment creation failed:", error);
        alert(`Creation failed: ${error.message}`);
    }
};

    // ----------------------------------------------------
    // CONDITIONAL INPUT RENDERERS
    // ----------------------------------------------------
    const renderNetworkInputs = () => (
        <Form.Group className="mb-3">
            {formData.networkUsername ? (
                // Selected Patient Confirmation Card
                <Card className="border-primary bg-light">
                    <Card.Body className="d-flex justify-content-between align-items-center py-2 px-3">
                        <div>
                            <small className="text-muted d-block">Selected Patient:</small>
                            <strong>{formData.manualName}</strong>{" "}
                            <span className="text-primary">(@{formData.networkUsername})</span>
                        </div>
                        <Button 
                            variant="outline-danger" 
                            size="sm" 
                            onClick={handleClearSelectedPatient}
                        >
                            Change
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                // Search Bar displayed when no patient is selected
                <WhitelistUserSearchBar
                    onSearch={(query) => searchMaskedPatientName(query)}
                    onUserFound={(user) => {
                        setFormData((prev) => ({
                            ...prev,
                            networkUsername: user.username,
                            manualName: user.name, // Used solely for UI visual feedback
                        }));
                    }}
                    clearOnSelect={true}
                />
            )}
        </Form.Group>
    );

    const renderExternalInputs = () => (
        <>
            <Form.Group className="mb-3">
                <Form.Label>{VocabAppointments.external_patient_name[lan]}</Form.Label>
                <Form.Control 
                    type="text" 
                    name="manualName" 
                    placeholder={VocabAppointments.external_patient_name_placeholder[lan]}
                    value={formData.manualName} 
                    onChange={handleChange} 
                    required={!isNetworkPatient}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>{VocabAppointments.external_phone_number[lan]}</Form.Label>
                <Form.Control 
                    type="tel" 
                    placeholder={VocabAppointments.external_phone_number_placeholder[lan]}
                    name="manualPhoneNumber" 
                    value={formData.manualPhoneNumber} 
                    onChange={handleChange} 
                    required={!isNetworkPatient}
                />
            </Form.Group>
        </>
    );

    const PatientTypeToggle = () => (
        <Form.Group className="mb-3 d-flex flex-column align-items-center"> 
            <Form.Label className="mb-2">
                {VocabAppointments.patient_type[lan]}
            </Form.Label>
            
            <ButtonGroup className="w-100 d-flex">
                <Button 
                    variant={isNetworkPatient ? 'primary' : 'outline-secondary'}
                    onClick={() => handleTogglePatientType(true)}
                    className="w-50 text-nowrap"
                >
                    {VocabAppointments.network_patient[lan]}
                </Button>
                <Button 
                    variant={!isNetworkPatient ? 'primary' : 'outline-secondary'}
                    onClick={() => handleTogglePatientType(false)}
                    className="w-50 text-nowrap"
                >
                    {VocabAppointments.external_patient[lan]}
                </Button>
            </ButtonGroup>
        </Form.Group>
    );

    return (
        <Modal show={isOpen} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{VocabAppointments.add_appointment[lan]}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleCreate}>
                <Modal.Body>
                    {/* Title */}
                    <Form.Group className="mb-3">
                        <Form.Label>{VocabAppointments.title[lan]}</Form.Label>
                        <Form.Control 
                            type="text" 
                            name="title" 
                            placeholder={VocabAppointments.title_placeholder[lan]}
                            value={formData.title} 
                            onChange={handleChange} 
                            required
                        />
                    </Form.Group>
                    
                    {/* Start/End Time Row */}
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{VocabAppointments.start_time[lan]}</Form.Label>
                                <Form.Control 
                                    type="datetime-local" 
                                    name="start" 
                                    value={formData.start} 
                                    onChange={handleChange} 
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3"> 
                                <Form.Label>{VocabAppointments.end_time[lan]}</Form.Label>
                                <Form.Control 
                                    type="datetime-local" 
                                    name="end" 
                                    value={formData.end} 
                                    onChange={handleChange} 
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Patient Type Segmented Toggle */}
                    <PatientTypeToggle />

                    {/* Network Patient Inputs */}
                    <Collapse in={isNetworkPatient}>
                        <div id="network-inputs-container">
                            {renderNetworkInputs()}
                        </div>
                    </Collapse>

                    {/* External Patient Inputs */}
                    <Collapse in={!isNetworkPatient}>
                        <div id="external-inputs-container">
                            {renderExternalInputs()}
                        </div>
                    </Collapse>

                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>
                        {VocabButtons.cancel?.[lan]}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isCreating}>
                        {isCreating ? VocabButtons.creating[lan] : VocabButtons.save[lan]}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AppointmentFormModal;